import React from 'react'
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'

import { TableGraphSelection, TableViewConfig } from '../../../types/gotypes'
import { PlatinumData } from '../../../types/carbon/models/PlatinumData'
import {
  buildPositionalSelection,
  disambiguateBaseLabels,
  effectiveSelectionPositions,
} from '../../../utils/tableSelection'
import {
  AxisNode,
  axisLeafNodes,
  buildSideTree,
  buildTopTree,
  flattenTree,
  hiddenLeafPositions,
  resolveGroupLabelVisibility,
} from '../../../utils/tableViewConfig'
import { SelectDataDropdown } from './SelectDataRow'
import {
  extractRowTypes,
  extractColumnTypes,
} from '../../platinum-graphs/utils/convertPlatinumData'

interface TableSelectDataPanelProps {
  data: PlatinumData
  selection: TableGraphSelection | undefined
  viewConfig: TableViewConfig | undefined
  /**
   * One combined callback — a single edit can touch both the selection and
   * the view config (the first positional edit retires the legacy blocklist),
   * and two separate parent updates in one tick would clobber each other.
   */
  onChange: (
    selection: TableGraphSelection | undefined,
    viewConfig: TableViewConfig | undefined,
  ) => void
}

/** Leaf vec positions under a node (the node itself when it is a leaf). */
function leafPositions(node: AxisNode): number[] {
  return flattenTree([node])
    .filter(n => n.vecIndex !== undefined)
    .map(n => n.vecIndex!)
}

/**
 * Display label for a dropdown entry (#969 Obs 13). A group's own label is
 * often Carbon filter syntax (`!BrandUsage …(Sky)`), which must not leak into
 * the UI. When the group contains exactly one non-base leaf, that leaf's label
 * ("Sky") is the name the user knows the row by; otherwise the group label is
 * genuinely a heading and stays.
 */
function friendlyNodeLabel(node: AxisNode, vecTypes: string[]): string {
  const dataLeaves = flattenTree([node])
    .filter(n => n.vecIndex !== undefined)
    .filter(n => (vecTypes[n.vecIndex!] || '').toLowerCase() !== 'base')
  if (dataLeaves.length === 1 && dataLeaves[0].label) return dataLeaves[0].label
  return node.label || '(unlabelled)'
}

/**
 * A dropdown entry's vec type. An entry is a Base — a case-base row — when
 * every vec under it is one; a group holding a base and a data leaf is named
 * after the data leaf and reads as data.
 */
function nodeVecType(node: AxisNode, vecTypes: string[]): string {
  const types = flattenTree([node])
    .filter(n => n.vecIndex !== undefined)
    .map(n => (vecTypes[n.vecIndex!] || ''))
  return types.length > 0 && types.every(t => t.toLowerCase() === 'base') ? 'Base' : 'Code'
}

function tally(labels: string[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const label of labels) counts.set(label, (counts.get(label) ?? 0) + 1)
  return counts
}

/**
 * Labels for one axis's dropdown entries.
 *
 * Three passes, each fixing a way the list can be ambiguous:
 *  1. `friendlyNodeLabel` — never show Carbon filter syntax (#969 Obs 13).
 *  2. Base rows repeat one label per group ("Cases WF" × 10), so a REPEATED
 *     base label takes its group-mate's name: "Cases WF (Sky)" (#969 comment 2
 *     item 2, via the helper the chart editor left behind). A base label that
 *     occurs once is already unambiguous and is left alone.
 *  3. Any label still repeating — the dropdown now lists individual rows, and
 *     "18-24" appears under both Male and Female — is qualified with its
 *     parent's label.
 */
function optionLabels(nodes: AxisNode[], vecTypes: string[]): string[] {
  const friendly = nodes.map(node => friendlyNodeLabel(node, vecTypes))
  const types = nodes.map(node => nodeVecType(node, vecTypes))

  const withBases = disambiguateBaseLabels(friendly, types)
  const friendlyCounts = tally(friendly)
  const deduped = friendly.map((label, i) =>
    (friendlyCounts.get(label) ?? 0) > 1 ? withBases[i] : label)

  const counts = tally(deduped)
  return deduped.map((label, i) => {
    if ((counts.get(label) ?? 0) < 2) return label
    const path = nodes[i].path
    const parent = path[path.length - 2]
    return parent ? `${parent} · ${label}` : label
  })
}

/** True when the config carries nothing worth saving. */
function isEmptyConfig(config: TableViewConfig): boolean {
  return !config.hidden_rows?.length
    && !config.hidden_columns?.length
    && !config.row_order?.length
    && !config.column_order?.length
    && !Object.keys(config.column_widths || {}).length
    && config.freeze_headers === undefined
    && !config.hide_structural_labels
    // An explicit `false` is a real setting — group labels hidden — and must
    // never be discarded as noise.
    && config.show_row_group_labels === undefined
    && config.show_column_group_labels === undefined
}

/**
 * The table widget's "Select Data" panel (#969 Obs 12/13, reworked by #969
 * comment 2): a Rows dropdown and a Columns dropdown, each with its own
 * group-label toggle directly beneath it. The nested blocklist checkbox tree
 * it replaces exposed Carbon's internal structure; this shows a FLAT list of
 * row labels and column labels, no hierarchy.
 *
 * Three round-2 decisions live here:
 *
 * - **What the list contains** (comment 2 item 1). One entry per LEAF, in vec
 *   order (`axisLeafNodes`) — the list maps one-to-one onto the rows the table
 *   can display. Listing a hierarchy LEVEL instead failed both ways: a table
 *   whose rows all sit under one group collapsed to a single useless entry,
 *   and the real Sky exec table stopped at its ten brand groups, hiding the
 *   base row inside each one.
 * - **Case bases are selectable** (comment 2 item 2) — the deliberate inverse
 *   of the chart, which excludes them (#968 comment 3). A table is where a
 *   user pulls a base row back in, so the bases are offered, disambiguated by
 *   group-mate. They stay DESELECTED by default on fresh widgets
 *   (`defaultDataOnlySelection`, applied by the editor): available, not on.
 *   Because the entries are leaves, a base the table currently draws is also
 *   present and TICKED, so it can be turned off again.
 * - **No Switch Axis** (comment 2 item 3). Transposing a table that shows
 *   column percents makes the figures wrong — "45% of males were in Feb-26",
 *   proportions summing past 100% across periods. That is a data-integrity
 *   trap, not a display toggle. The CHART keeps it (a chart's transpose is a
 *   re-plot, not a re-reading of percentages). A widget that already stored
 *   `switch_axis` keeps rendering transposed and keeps the flag through every
 *   edit here — silently changing a saved widget's output would be worse than
 *   the trap.
 *
 * Selection is POSITIONAL throughout (Obs 12, mirroring #968 Obs 11): each
 * dropdown entry is a node of the axis, and toggling it selects or deselects
 * the vec positions underneath it; the widget stores vec positions on
 * `selection` exactly as the chart does. Legacy `view_config` hide lists still
 * display (mapped onto the positions they hide today) — the first edit through
 * this control rewrites the widget positionally and retires them. Everything
 * here is DISPLAY level: the source table, its spec, and Carbon settings are
 * never touched.
 */
export const TableSelectDataPanel: React.FC<TableSelectDataPanelProps> = ({
  data,
  selection,
  viewConfig,
  onChange,
}) => {
  // The FULL hierarchy drives the legacy blocklist mapping and the labels'
  // parent qualification; the dropdowns list its LEAVES — one entry per row or
  // column the table can draw (#969 comment 2 items 1 and 2).
  const rowHierarchy = buildSideTree(data)
  const columnHierarchy = buildTopTree(data)
  const rowTree = axisLeafNodes(rowHierarchy)
  const columnTree = axisLeafNodes(columnHierarchy)
  const rowVecTypes = extractRowTypes(data)
  const columnVecTypes = extractColumnTypes(data)
  const rowLeafCount = rowTree.length
  const columnLeafCount = columnTree.length

  // Effective vec positions: the positional selection when there is one;
  // otherwise a legacy view_config hide list mapped onto today's positions.
  const base = effectiveSelectionPositions(selection, data)
  const legacyHiddenRows = hiddenLeafPositions(rowHierarchy, viewConfig?.hidden_rows)
  const legacyHiddenCols = hiddenLeafPositions(columnHierarchy, viewConfig?.hidden_columns)
  const effectiveRowVecs = base.rowPositions.length > 0
    ? base.rowPositions
    : legacyHiddenRows.length > 0
      ? Array.from({ length: rowLeafCount }, (_, i) => i).filter(i => !legacyHiddenRows.includes(i))
      : []
  const effectiveColumnVecs = base.columnPositions.length > 0
    ? base.columnPositions
    : legacyHiddenCols.length > 0
      ? Array.from({ length: columnLeafCount }, (_, i) => i).filter(i => !legacyHiddenCols.includes(i))
      : []

  // A dropdown entry is checked when ANY of its vecs is shown ("all" — an
  // empty effective list — checks everything).
  const nodeChecked = (node: AxisNode, effective: number[]): boolean => {
    if (effective.length === 0) return true
    const leaves = leafPositions(node)
    return leaves.length === 0 || leaves.some(p => effective.includes(p))
  }

  const checkedEntries = (tree: AxisNode[], effective: number[]): number[] => {
    const checked = tree
      .map((node, index) => ({ node, index }))
      .filter(({ node }) => nodeChecked(node, effective))
      .map(({ index }) => index)
    return checked.length === tree.length ? [] : checked
  }

  const rowPositions = checkedEntries(rowTree, effectiveRowVecs)
  const columnPositions = checkedEntries(columnTree, effectiveColumnVecs)
  // Switch Axis has no control here any more (#969 comment 2 item 3), but a
  // widget that already stored a transpose keeps it: dropping the flag on an
  // unrelated edit would silently change a saved widget's output.
  const switchAxis = selection?.switch_axis || false

  /**
   * Converts a dropdown pick back to vec positions. Entries that stay checked
   * keep whatever subset of their vecs was already shown (so a default that
   * excluded nested base rows survives unrelated toggles); newly checked
   * entries bring their whole branch; unchecked entries drop theirs.
   */
  const toVecPositions = (
    tree: AxisNode[],
    picked: number[],
    effective: number[],
    leafCount: number,
  ): number[] => {
    const pickedSet = new Set(picked.length > 0 ? picked : tree.map((_, i) => i))
    if (pickedSet.size === tree.length) {
      // Everything checked: preserve any partial subsets, or
      // "all" when nothing was filtered.
      if (effective.length === 0) return []
    }
    const out: number[] = []
    tree.forEach((node, index) => {
      if (!pickedSet.has(index)) return
      const leaves = leafPositions(node)
      const wasChecked = nodeChecked(node, effective)
      if (wasChecked && effective.length > 0) {
        out.push(...leaves.filter(p => effective.includes(p)))
      } else {
        out.push(...leaves)
      }
    })
    return out.length === leafCount ? [] : out.sort((a, b) => a - b)
  }

  /**
   * The first positional edit retires the legacy blocklist fields — the
   * selection now carries the same intent, and leaving both would apply two
   * filters. Widths, freeze, and the structural-labels toggle survive.
   */
  const retiredViewConfig = (): TableViewConfig | undefined => {
    if (!viewConfig) return undefined
    const {
      hidden_rows: _hr,
      hidden_columns: _hc,
      row_order: _ro,
      column_order: _co,
      ...rest
    } = viewConfig
    return isEmptyConfig(rest) ? undefined : rest
  }

  const emit = (rowVecs: number[], columnVecs: number[], nextSwitchAxis: boolean) => {
    onChange(
      buildPositionalSelection(rowVecs, columnVecs, nextSwitchAxis, data),
      retiredViewConfig(),
    )
  }

  /**
   * Writes one side's group-label toggle (#969 comment 2 item 4). Setting the
   * row side explicitly also drops the legacy `hide_structural_labels`: it
   * said the same thing about the same columns, and a config carrying both
   * would be two answers to one question.
   */
  const handleGroupLabelsToggle = (axis: 'rows' | 'columns', show: boolean) => {
    const next: TableViewConfig = { ...viewConfig }
    if (axis === 'rows') {
      next.show_row_group_labels = show
      delete next.hide_structural_labels
    } else {
      next.show_column_group_labels = show
    }
    onChange(selection, isEmptyConfig(next) ? undefined : next)
  }

  const groupLabels = resolveGroupLabelVisibility(viewConfig)
  const hasRowGroups = (data.side?.groups || []).length > 0
  const hasColumnGroups = (data.top?.groups || []).length > 0

  /**
   * One axis: its dropdown with its group-label toggle directly beneath
   * (#969 comment 2 item 4). The column stretches its children, so the
   * dropdown ends up exactly as wide as the checkbox row under it rather
   * than sitting at its own narrow intrinsic width.
   */
  const axisGroup = (
    testId: string,
    dropdown: React.ReactNode,
    toggle: React.ReactNode,
  ) => (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5 }}
    >
      {dropdown}
      {toggle}
    </Box>
  )

  const groupLabelToggle = (axis: 'rows' | 'columns', label: string) => (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={axis === 'rows' ? groupLabels.rows : groupLabels.columns}
          onChange={(e) => handleGroupLabelsToggle(axis, e.target.checked)}
        />
      }
      label={<Typography variant="body2">{label}</Typography>}
      sx={{ ml: 0, mr: 0 }}
    />
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Rows and its toggle, a gap, then Columns and its toggle. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, flexWrap: 'wrap' }}>
        {axisGroup(
          'rows-select-group',
          <SelectDataDropdown
            noun="Row"
            fullWidth
            options={optionLabels(rowTree, rowVecTypes)}
            selected={rowPositions}
            onChange={(picked) =>
              emit(
                toVecPositions(rowTree, picked, effectiveRowVecs, rowLeafCount),
                effectiveColumnVecs,
                switchAxis,
              )}
          />,
          hasRowGroups && groupLabelToggle('rows', 'Show row group labels'),
        )}
        {axisGroup(
          'columns-select-group',
          <SelectDataDropdown
            noun="Column"
            fullWidth
            options={optionLabels(columnTree, columnVecTypes)}
            selected={columnPositions}
            onChange={(picked) =>
              emit(
                effectiveRowVecs,
                toVecPositions(columnTree, picked, effectiveColumnVecs, columnLeafCount),
                switchAxis,
              )}
          />,
          hasColumnGroups && groupLabelToggle('columns', 'Show column group labels'),
        )}
      </Box>
    </Box>
  )
}

export default TableSelectDataPanel
